'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <section className="bg-primary/10 py-12">
        <div className="container mx-auto px-4">
          <Link href="/">
            <Button variant="ghost" className="mb-8 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Learn how we collect, use, and protect your personal information.
          </p>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold mb-4">1. Introduction and Overview</h2>
            <p>
              Welcome to Kawkb! Protecting your privacy is a priority for us. This Privacy Policy
              outlines how we collect, use, store, and share your personal information when you use
              our website (the "Site"), mobile apps (the "Apps"), or any of our services
              (collectively, the "Services"). We are committed to maintaining transparency and
              building trust with our community.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">2. Information We Collect</h2>
            <p>We may collect the following types of information:</p>

            <h3 className="text-xl font-semibold mb-2 mt-4">a. Personal Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Contact details:</strong> Name, email address, phone number, and payment
                information.
              </li>
              <li>
                <strong>Verification data:</strong> Government-issued IDs and profile photos for
                identity confirmation.
              </li>
              <li>
                <strong>Booking data:</strong> Stay history, preferences, and special requests.
              </li>
              <li>
                <strong>Communication records:</strong> Messages, reviews, and support interactions.
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-2 mt-4">b. Non-Personal Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Device data:</strong> IP address, browser type, and operating system.
              </li>
              <li>
                <strong>Usage data:</strong> Page views, clicks, session activity, and referral
                information.
              </li>
              <li>
                <strong>Cookies:</strong> Data collected through cookies and similar technologies to
                improve your experience.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">3. How We Collect Information</h2>
            <p>We gather data through the following means:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Direct Input:</strong> Information you provide during booking, registration,
                or communication.
              </li>
              <li>
                <strong>Automated Tools:</strong> Data from cookies and other tracking technologies
                used while browsing our Services.
              </li>
              <li>
                <strong>Third-Party Sources:</strong> Data obtained from social media logins,
                advertising partners, or analytics tools.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">4. How We Use Your Information</h2>
            <p>We utilize the collected data to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Deliver Services:</strong> Facilitate bookings, process payments, and
                support your experience.
              </li>
              <li>
                <strong>Personalize Your Experience:</strong> Customize your stays and communication
                preferences.
              </li>
              <li>
                <strong>Stay Connected:</strong> Send confirmations, updates, and relevant
                notifications.
              </li>
              <li>
                <strong>Marketing and Updates:</strong> Share promotions and newsletters (with your
                consent).
              </li>
              <li>
                <strong>Improve Our Platform:</strong> Analyze data to enhance usability and
                performance.
              </li>
              <li>
                <strong>Legal Compliance:</strong> Fulfill legal requirements and safeguard our
                community.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">5. How We Share Your Information</h2>
            <p>We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Service Partners:</strong> To process payments, host content, or analyze
                data.
              </li>
              <li>
                <strong>Affiliates:</strong> To provide consistent services within our network.
              </li>
              <li>
                <strong>Legal Authorities:</strong> When necessary for compliance or protecting
                rights.
              </li>
              <li>
                <strong>With Your Approval:</strong> When you explicitly consent to share your data
                (e.g., for joint promotions).
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">6. Data Security</h2>
            <p>
              We implement strong security practices to protect your data, including encryption and
              restricted access. While we strive to safeguard your information, no system is
              entirely foolproof.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">7. Data Retention</h2>
            <p>
              We retain your personal data only as long as needed to provide services or meet legal
              obligations. When no longer required, we securely delete or anonymize it.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">8. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access your personal data.</li>
              <li>Correct any inaccuracies.</li>
              <li>Request deletion when applicable.</li>
              <li>Opt-out of marketing communications.</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact us at{' '}
              <a href="mailto:support@kawkb.com" className="text-blue-600 hover:underline">
                support@kawkb.com
              </a>
              .
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">9. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies to enhance your experience. You can manage your preferences through
              browser settings. For more details, please see our{' '}
              <Link href="/cookies" className="text-blue-600 hover:underline">
                Cookies Policy
              </Link>{' '}
              page.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">10. Children's Privacy</h2>
            <p>
              Our services are intended for users aged 18 and above. We do not knowingly collect
              data from minors. If you believe we have inadvertently gathered such information,
              contact us immediately for removal.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">11. Third-Party Links</h2>
            <p>
              Our platform may contain links to external sites. Please note that we do not control
              their privacy practices. We recommend reviewing their policies separately.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">12. Policy Updates</h2>
            <p>
              We may update this Privacy Policy periodically. Significant changes will be
              communicated via email or platform notifications. Please check this page regularly for
              updates.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">13. Contact Us</h2>
            <p>For questions or concerns about your privacy, reach out to us at:</p>
            <ul className="list-none pl-6 space-y-1 mt-2">
              <li>
                <strong>Email:</strong>{' '}
                <a href="mailto:support@kawkb.com" className="text-blue-600 hover:underline">
                  support@kawkb.com
                </a>
              </li>
              <li>
                <strong>Phone:</strong>{' '}
                <a href="tel:+201021514433" className="text-blue-600 hover:underline">
                  +201021514433
                </a>
              </li>
            </ul>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                Last updated:{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
